import { useEffect } from "react";

const C = {
  bg: "#FAF7F2",
  white: "#FFFFFF",
  orange: "#E87C0C",
  dark: "#1A1208",
  charcoal: "#2D2416",
  muted: "#5C4D3C",
  border: "#EDE5D8",
};

const sections = [
  {
    id: "how-to-request",
    title: "How to Request Account Deletion",
    steps: [
      "Sending an email to swaadindia.app@gmail.com with the subject \"Account Deletion Request\".",
      "Calling our support team at 7030078393 or 7276857746.",
      "Providing the phone number or email associated with your SwaadIndia account.",
    ],
  },
  {
    id: "data-deleted",
    title: "What Data Will Be Deleted",
    intro: "Upon verification and processing of your request, we will delete:",
    items: [
      "Account profile information",
      "Name",
      "Email address",
      "Phone number",
      "Saved addresses",
      "App preferences",
    ],
  },
  {
    id: "data-retained",
    title: "Data That May Be Retained",
    intro: "Certain information may be retained for legal, security, fraud prevention, accounting, taxation, dispute resolution, and regulatory compliance purposes, including:",
    items: [
      "Transaction and order records",
      "Payment references",
      "Support communications",
    ],
    note: "Such information will be retained only for the period required by applicable laws or legitimate business requirements.",
  },
  {
    id: "processing-time",
    title: "Processing Time",
    content: "Account deletion requests are typically processed within 30 days of verification.",
  },
  {
    id: "contact",
    title: "Contact Information",
    contact: {
      name: "SwaadIndia Support",
      email: "swaadindia.app@gmail.com",
      phone: "7030078393 / 7276857746",
      website: "https://swaadindia.co.in",
    },
  },
];

export default function AccountDeletion({ onBack }) {
  useEffect(() => {
    document.title = "Delete Account — SwaadIndia";
    window.scrollTo(0, 0);
    return () => { document.title = "SwaadIndia — Mess Management Platform"; };
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div style={{ fontFamily: "'Jost', sans-serif", background: C.bg, color: C.dark, minHeight: "100vh" }}>

      {/* Header */}
      <header style={{ background: C.white, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 100, padding: "0 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/swaadIndia_logo.png" alt="SwaadIndia" style={{ height: 40, width: "auto" }} />
            <span style={{ background: "#FEF3E2", color: C.orange, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.5px" }}>Account Deletion</span>
          </div>
          {onBack && (
            <button onClick={onBack} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 18px", fontSize: 14, color: C.muted, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
              &#8592; Back to Site
            </button>
          )}
        </div>
      </header>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${C.dark} 0%, #2D2416 100%)`, color: "#fff", padding: "60px 24px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.6, marginBottom: 12 }}>Account &middot; Data</p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 700, margin: "0 0 16px", lineHeight: 1.1 }}>Delete Your SwaadIndia Account</h1>
          <p style={{ fontSize: 16, opacity: 0.8, maxWidth: 520, margin: "0 0 20px", lineHeight: 1.7 }}>SwaadIndia allows users to request deletion of their account and associated personal data.</p>
          <span style={{ fontSize: 13, opacity: 0.7, background: "rgba(255,255,255,0.1)", display: "inline-block", padding: "6px 14px", borderRadius: 6 }}>Last Updated: <strong>June 22, 2026</strong></span>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 24px", display: "grid", gridTemplateColumns: "220px 1fr", gap: 48, alignItems: "start" }}>

        {/* TOC */}
        <aside style={{ position: "sticky", top: 84, background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px 16px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: C.muted, marginBottom: 14 }}>On this page</p>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {sections.map(s => (
              <li key={s.id}>
                <button onClick={() => scrollTo(s.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.muted, padding: "6px 0", display: "block", textAlign: "left", width: "100%", lineHeight: 1.4, fontFamily: "inherit" }}>
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Content */}
        <article>
          {sections.map(section => (
            <section key={section.id} id={section.id} style={{ paddingBottom: 40, marginBottom: 40, borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: C.dark, marginBottom: 16 }}>{section.title}</h2>

              {/* Numbered steps */}
              {section.steps && (
                <ol style={{ paddingLeft: 20, margin: 0 }}>
                  {section.steps.map((step, i) => (
                    <li key={i} style={{ fontSize: 15, color: C.muted, padding: "8px 0", borderBottom: `1px solid ${C.border}`, lineHeight: 1.7 }}>
                      {step.includes("swaadindia.app@gmail.com")
                        ? <>{step.split("swaadindia.app@gmail.com")[0]}<a href="mailto:swaadindia.app@gmail.com" style={{ color: C.orange, textDecoration: "none", fontWeight: 500 }}>swaadindia.app@gmail.com</a>{step.split("swaadindia.app@gmail.com")[1]}</>
                        : step}
                    </li>
                  ))}
                </ol>
              )}

              {/* Intro text */}
              {section.intro && (
                <p style={{ fontSize: 15, color: C.muted, marginBottom: 14, lineHeight: 1.75 }}>{section.intro}</p>
              )}

              {/* Bullet items */}
              {section.items && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {section.items.map((item, i) => (
                    <li key={i} style={{ display: "flex", gap: 10, fontSize: 15, color: C.muted, padding: "8px 0", borderBottom: `1px solid ${C.border}`, lineHeight: 1.6 }}>
                      <span style={{ color: C.orange, fontWeight: 700, flexShrink: 0 }}>&#10003;</span>{item}
                    </li>
                  ))}
                </ul>
              )}

              {/* Note */}
              {section.note && (
                <p style={{ fontSize: 14, color: C.muted, marginTop: 16, padding: "12px 16px", background: "#FEF3E2", borderLeft: `3px solid ${C.orange}`, borderRadius: "0 8px 8px 0", lineHeight: 1.7 }}>
                  {section.note}
                </p>
              )}

              {/* Plain text */}
              {section.content && (
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.75 }}>{section.content}</p>
              )}

              {/* Contact block */}
              {section.contact && (
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px", marginTop: 8 }}>
                  {[
                    { label: "App", value: section.contact.name },
                    { label: "Email", value: section.contact.email, href: `mailto:${section.contact.email}` },
                    { label: "Phone", value: section.contact.phone },
                    { label: "Website", value: section.contact.website, href: section.contact.website },
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", gap: 16, fontSize: 15, color: C.muted, marginBottom: 12, alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: C.muted, minWidth: 64 }}>{row.label}</span>
                      {row.href
                        ? <a href={row.href} style={{ color: C.orange, textDecoration: "none", fontWeight: 500 }}>{row.value}</a>
                        : <span>{row.value}</span>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </div>

      {/* Footer */}
      <div style={{ background: C.charcoal, padding: "32px 24px", textAlign: "center" }}>
        <p style={{ color: "#9a8070", fontSize: 13, margin: "0 0 12px" }}>&#169; {new Date().getFullYear()} SwaadIndia &middot; Premontix. All rights reserved.</p>
        {onBack && (
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "8px 20px", fontSize: 13, color: "#c8b4a0", cursor: "pointer", fontFamily: "inherit" }}>
            &#8592; Back to SwaadIndia
          </button>
        )}
      </div>
    </div>
  );
}
